import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import { errorHandler } from 'App/utils';
import toast from 'Shared/toast';
import useUpdateReferralMedication from 'App/hooks/useUpdateReferralMedication';
import useGetReferralMedication from 'App/hooks/useGetReferralMedication';
import { referralModalPropType } from 'App/components/AddReferralActivity/types';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import EditReferralMedicationTable from './EditReferralMedicationTable';
import * as R from 'ramda';
import dayjs from 'Shared/dayjs';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner';

const defaultProps = {};

const EditMedicationModal = ({ show, onHide, onUpdate, patientId, referralId }) => {
    const updateMedicationMutation = useUpdateReferralMedication();
    const { data: medications, isLoading } = useGetReferralMedication(patientId, referralId);

    const initialValues = useMemo(() => {
        if (isLoading) {
            return {};
        }
        // prettier-ignore
        return R.compose(
            R.assoc('medications', R.__, {}),
            R.indexBy(R.prop('medicationId')),
            R.map(
                R.applySpec({
                    id: R.prop('id'),
                    status: R.ifElse(R.pipe(R.prop('status'), R.complement(R.isNil)), R.applySpec({
                        valueName: R.prop('status')
                    }), R.always(null)),
                    statusDate: R.compose(
                        d => d ? dayjs.utc(d).toDate() : null,
                        R.prop('statusDate')
                    ),
                    medicationId: R.prop('medicationId'),
                    rxNumber: R.ifElse(R.hasPath(['rx']), R.applySpec({
                        id: R.path(['rx', 'id']),
                        rxNumber: R.path(['rx', 'rxNumber']),
                    }),R.ifElse(
                        R.both(
                        R.pathSatisfies(R.is(Array), ['medication', 'rxNumbers']),
                        R.pathSatisfies(arr => arr.length === 1, ['medication', 'rxNumbers'])
                    ),
                    R.applySpec({
                        id: R.path(['medication', 'rxNumbers', 0, 'id']),
                        rxNumber: R.path(['medication', 'rxNumbers', 0, 'rxNumber']),
                    }),
                    R.always(null)
                    )),
                })
            )
        )(medications);
    }, [isLoading, medications]);

    if (isLoading) {
        return <TherigyLoadingSpinner />;
    }
    return (
        <Modal size="lg" show={show} onHide={() => onHide()} backdrop="static" className="referral-add-communication">
            <Modal.Header>
                <Modal.Title role="heading">Edit Medications</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    const { medications: formMedicationValue } = values;
                    const promiseArray = Object.values(formMedicationValue || {}).map((med) => {
                        const statusDate = med?.statusDate
                            ? dayjs(med?.statusDate)
                                  .utc()
                                  .set('hour', 12)
                                  .set('minute', 1)
                                  .set('second', 0)
                                  .set('millisecond', 0)
                                  .toISOString()
                            : dayjs().toISOString();

                        const body = {
                            medication_id: med.medicationId,
                            status: med?.status?.valueName,
                            status_date: statusDate,
                        };

                        if (med?.rxNumber) {
                            body.rx_number_id = med?.rxNumber?.id;
                        }

                        return updateMedicationMutation.mutateAsync({
                            patientId,
                            referralId,
                            medicationId: med.id,
                            body,
                        });
                    });
                    Promise.all(promiseArray)
                        .then(() => {
                            onUpdate();
                            toast.success('Referral medication successfully updated');
                        })
                        .catch(errorHandler)
                        .finally(() => {
                            setSubmitting(false);
                            onHide();
                        });
                }}
            >
                {({ isSubmitting, handleSubmit, submitForm, isValid }) => {
                    return (
                        <form onSubmit={handleSubmit} noValidate>
                            <Modal.Body>
                                <EditReferralMedicationTable data={medications} isLoading={isLoading} insideModal />
                            </Modal.Body>
                            <Modal.Footer>
                                <FormButtonGroup>
                                    <Button
                                        variant="outline-secondary"
                                        disabled={isSubmitting}
                                        onClick={() => onHide(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="save-due-date"
                                        onClick={submitForm}
                                        id="add-referral-communication-modal-submit"
                                        disabled={!isValid || isSubmitting}
                                    >
                                        Save
                                    </Button>
                                </FormButtonGroup>
                            </Modal.Footer>
                        </form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

EditMedicationModal.propTypes = { ...referralModalPropType, onUpdate: PropTypes.func.isRequired };
EditMedicationModal.defaultProps = defaultProps;

export default EditMedicationModal;
